/**
 * UniversalTableRow - Data-agnostic table row component
 *
 * This component renders a table row using a cell-based configuration.
 * It remains completely agnostic of the data it displays - all cell content
 * and behavior is defined by the CellConfig[] passed from the parent.
 */

import { cn } from '@/lib/utils';
import { getCellContainerClass, getRowContainerClass } from '../utils/tableStyles';

export interface CellConfig {
  id: string;
  content: React.ReactNode;
  colSpan?: number;
  align?: 'left' | 'center' | 'right';
  hidden?: boolean;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
  style?: React.CSSProperties; // Add style support for inline styles (e.g., sticky positioning)
  /** Optional className applied to the entire row container. Set on the first cell to take effect. */
  rowClassName?: string;
}

interface UniversalTableRowProps {
  ticket: any; // Generic row data
  onClick: () => void;
  cells?: CellConfig[]; // Cell-based rendering configuration
  listTemplateColumns?: string;
  isSelected?: boolean;
  onToggleSelect?: (selected: boolean) => void;
  onClearSelection?: () => void;
  selectedTicketIds?: number[];
  isBulkActive?: boolean;
  // Legacy props for backwards compatibility (unused when cells are provided)
  onComplete?: (ticketId: number) => void;
  onUpdate?: (ticketId: number, updates: any) => void;
  onViewEmails?: (ticket: any, communicationMode?: 'tenant' | 'supplier') => void;
  onQuickNotes?: (ticket: any) => void;
  onCreateInsurance?: (ticket: any) => void;
  onMerge?: (ticket: any) => void;
  onDelete?: (ticket: any) => void;
  onMoveToCompletion?: (ticketId: number) => Promise<void>;
  onRevertToOpen?: (ticketId: number) => Promise<void>;
  onSmartInsights?: (ticket: any) => void;
}

const UniversalTableRow = ({
  onClick,
  cells,
  listTemplateColumns,
  isSelected = false
}: UniversalTableRowProps) => {
  // If no cells are provided, show empty state
  if (!cells || cells.length === 0) {
    return (
      <div className="px-4 py-8 text-center text-neutral-secondary-mid">
        <p className="text-sm">No cell configuration provided for this row</p>
      </div>
    );
  }

  // Render cells — merge optional rowClassName from first cell
  const rowClassName = cells[0]?.rowClassName;
  return (
    <div
      data-universal-row
      className={cn(getRowContainerClass(isSelected, false), rowClassName)}
      style={listTemplateColumns ? { gridTemplateColumns: listTemplateColumns } : undefined}
      onClick={onClick}
    >
      {cells.map((cell, index) => {
        return (
          <div
            key={cell.id || `cell-${index}`}
            className={getCellContainerClass(cell.colSpan || 1, cell.hidden || false, cell.align, cell.className)}
            onClick={cell.onClick}
            style={cell.style}
          >
            {cell.content}
          </div>
        );
      })}
    </div>
  );
};

export default UniversalTableRow;
